/**
 * SiteLayerDrawOverlay — native click-to-add-vertex drawing on a parent
 * <Map>. Avoids google.maps.drawing.DrawingManager (deprecated May 2026).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { Check, RotateCcw, Undo2, X } from 'lucide-react';

import { Button } from '../ui/button';
import { latLngFromGooglePath, minVerticesForGeometry } from '../../lib/siteLayerEditor';

export type DrawToolMode = 'polygon' | 'line' | 'edit-polygon' | 'edit-line' | 'edit-point' | null;

export interface SiteLayerDrawOverlayProps {
  mode: DrawToolMode;
  strokeColor: string;
  /** Seed vertices when editing an existing layer. */
  initialCoordinates?: { lat: number; lng: number }[];
  onComplete: (coordinates: { lat: number; lng: number }[]) => void;
  onCancel: () => void;
}

export const SiteLayerDrawOverlay: React.FC<SiteLayerDrawOverlayProps> = ({
  mode,
  strokeColor,
  initialCoordinates,
  onComplete,
  onCancel,
}) => {
  const map = useMap();
  const [vertices, setVertices] = useState<{ lat: number; lng: number }[]>([]);
  const previewRef = useRef<google.maps.Polygon | google.maps.Polyline | null>(null);
  const editOverlayRef = useRef<google.maps.Polygon | google.maps.Polyline | null>(null);
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  const geometryType = mode === 'edit-point'
    ? 'point'
    : mode === 'line' || mode === 'edit-line'
      ? 'line'
      : 'polygon';
  const isEdit = mode === 'edit-polygon' || mode === 'edit-line' || mode === 'edit-point';
  const active = mode !== null;

  const clearPreview = useCallback(() => {
    previewRef.current?.setMap(null);
    previewRef.current = null;
  }, []);

  const clearEditOverlay = useCallback(() => {
    editOverlayRef.current?.setMap(null);
    editOverlayRef.current = null;
  }, []);

  useEffect(() => {
    if (!active) {
      setVertices([]);
      clearPreview();
      clearEditOverlay();
      return;
    }
    if (isEdit && initialCoordinates?.length) {
      setVertices(initialCoordinates);
    } else if (!isEdit) {
      setVertices([]);
    }
  }, [active, isEdit, initialCoordinates, clearPreview, clearEditOverlay]);

  // Editable overlay for edit mode
  useEffect(() => {
    if (!map || !isEdit || mode === 'edit-point' || !initialCoordinates?.length || typeof google === 'undefined') {
      clearEditOverlay();
      return;
    }

    clearEditOverlay();
    if (geometryType === 'line') {
      editOverlayRef.current = new google.maps.Polyline({
        path: initialCoordinates,
        strokeColor,
        strokeWeight: 3,
        editable: true,
        map,
      });
    } else {
      editOverlayRef.current = new google.maps.Polygon({
        paths: initialCoordinates,
        strokeColor,
        strokeWeight: 2,
        fillColor: strokeColor,
        fillOpacity: 0.25,
        editable: true,
        map,
      });
    }

    return () => clearEditOverlay();
  }, [map, isEdit, mode, initialCoordinates, geometryType, strokeColor, clearEditOverlay]);

  // Preview while placing new vertices
  useEffect(() => {
    if (!map || !active || isEdit || vertices.length === 0 || typeof google === 'undefined') {
      if (!isEdit) clearPreview();
      return;
    }

    clearPreview();
    if (geometryType === 'line') {
      previewRef.current = new google.maps.Polyline({
        path: vertices,
        strokeColor,
        strokeWeight: 3,
        strokeOpacity: 0.9,
        map,
      });
    } else {
      previewRef.current = new google.maps.Polygon({
        paths: vertices,
        strokeColor,
        strokeWeight: 2,
        fillColor: strokeColor,
        fillOpacity: 0.2,
        map,
      });
    }

    return () => clearPreview();
  }, [map, active, isEdit, vertices, geometryType, strokeColor, clearPreview]);

  // Map clicks for new geometry
  useEffect(() => {
    if (!map || !active || (isEdit && mode !== 'edit-point')) return;

    clickListenerRef.current?.remove();
    clickListenerRef.current = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      const latLng = e.latLng;
      if (!latLng) return;
      const point = { lat: latLng.lat(), lng: latLng.lng() };
      setVertices(prev => mode === 'edit-point' ? [point] : [...prev, point]);
    });

    return () => {
      clickListenerRef.current?.remove();
      clickListenerRef.current = null;
    };
  }, [map, active, isEdit, mode]);

  const undoLast = () => setVertices(prev => prev.slice(0, -1));

  const finish = () => {
    if (isEdit && mode !== 'edit-point' && editOverlayRef.current) {
      const path =
        geometryType === 'line'
          ? (editOverlayRef.current as google.maps.Polyline).getPath()
          : (editOverlayRef.current as google.maps.Polygon).getPath();
      const coords = latLngFromGooglePath(path);
      if (coords.length >= minVerticesForGeometry(geometryType)) {
        onComplete(coords);
      }
      return;
    }
    if (vertices.length >= minVerticesForGeometry(geometryType)) {
      onComplete(vertices);
    }
  };

  const canFinish = mode === 'edit-point'
    ? vertices.length >= 1
    : isEdit
    ? (initialCoordinates?.length ?? 0) >= minVerticesForGeometry(geometryType)
    : vertices.length >= minVerticesForGeometry(geometryType);

  if (!active) return null;

  const min = minVerticesForGeometry(geometryType);

  return (
    <>
    {mode === 'edit-point' && vertices[0] && (
      <AdvancedMarker position={vertices[0]}>
        <span
          className="block w-4 h-4 rounded-full border-2 border-white shadow-md"
          style={{ backgroundColor: strokeColor }}
          aria-hidden
        />
      </AdvancedMarker>
    )}
    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 pointer-events-none">
      <p className="type-body font-bold uppercase tracking-widest text-text-primary bg-background/90 px-2 py-1 border border-border-subtle pointer-events-auto">
        {mode === 'edit-point'
          ? 'Click map to relocate point · Finish when done'
          : isEdit
          ? `Drag vertices · Finish when done (${min}+ points)`
          : `Click map to add points · ${vertices.length} placed (${min} min)`}
      </p>
      <div className="flex items-center gap-1 pointer-events-auto bg-background/95 border border-border-subtle shadow-md p-1">
        {!isEdit && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 rounded-md type-body uppercase tracking-widest max-[959px]:min-h-11"
            disabled={vertices.length === 0}
            onClick={undoLast}
            data-testid="layer-draw-undo"
          >
            <Undo2 className="w-3 h-3 mr-1" /> Undo point
          </Button>
        )}
        <Button
          type="button"
          variant="default"
          size="sm"
          className="h-7 rounded-md type-body uppercase tracking-widest max-[959px]:min-h-11"
          disabled={!canFinish}
          onClick={finish}
          data-testid="layer-draw-finish"
        >
          <Check className="w-3 h-3 mr-1" /> Finish
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 rounded-md type-body uppercase tracking-widest max-[959px]:min-h-11"
          onClick={onCancel}
          data-testid="layer-draw-cancel"
        >
          <X className="w-3 h-3 mr-1" /> Cancel
        </Button>
        {!isEdit && vertices.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 rounded-md type-body uppercase tracking-widest max-[959px]:min-h-11"
            onClick={() => setVertices([])}
            data-testid="layer-draw-clear"
          >
            <RotateCcw className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
      </div>
    </div>
    </>
  );
};
